import { LISTING_STATUSES, type ListingStatus } from '@carsfinders/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { AccessDenied } from '../../components/access-denied';
import { ApiError } from '../../lib/api';
import { t } from '../../lib/i18n';
import { toast } from '../../lib/toast';
import { approveListing, fetchAdminListings, rejectListing, transitionListing } from './api';
import { ModerationQueueTab } from './moderation-queue-tab';
import {
  addToQueue,
  bulkTargets,
  markQueueStatus,
  mergeQueue,
  removeFromQueue,
  statusParam,
  toQueueEntries,
  type QueueEntry,
  type QueueStatusFilter,
} from './moderation-queue';
import { OwnStockManager } from './own-stock-manager';
import { SellerListingsTab } from './seller-listings-tab';

type Tab = 'queue' | 'ownStock' | 'seller';
const TABS: Tab[] = ['queue', 'ownStock', 'seller'];

/** The status the queue opens on — the whole point of the screen. */
const DEFAULT_STATUS_FILTER: QueueStatusFilter = 'moderation';

/**
 * Resale-marketplace moderation — `Redesign-D-Admin`, screen «Маркетплейс».
 *
 * Two jobs under one heading, which is why the artboard puts them on tabs: the
 * queue of client listings waiting for a verdict, and the platform's own stock
 * turned into listings. The queue tab carries a count because the number is the
 * reason to open it. A third tab, «Мої оголошення», is the marketplace seller
 * cabinet moved here while the public `/sell` is hidden (2026-10-10).
 *
 * The queue is fed by `GET /api/admin/marketplace/listings?status=…`. It was
 * previously a browser-local registry of ids typed in by hand — the API had no
 * admin feed, so a moderator on a fresh browser saw an empty queue sitting over
 * a real backlog.
 *
 * The mobile artboard draws this screen READ-ONLY (the `market` branch of its
 * SIMPLE map, with the footnote «Публікація й повернення оголошень — у
 * десктопній версії»). It is implemented responsively instead, because refusing
 * a verdict on a phone is a limitation of the mock, not of the product.
 */
export function MarketplaceModerationPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('queue');
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [statusFilter, setStatusFilter] = useState<QueueStatusFilter>(DEFAULT_STATUS_FILTER);
  const [selected, setSelected] = useState<string[]>([]);
  const [accessDenied, setAccessDenied] = useState(false);

  const listingsQuery = useQuery({
    queryKey: ['admin-marketplace-listings', statusFilter],
    queryFn: () => fetchAdminListings({ status: statusParam(statusFilter), limit: 100 }),
  });

  /**
   * Fold each fetched page into the queue: the server wins for what it returned,
   * pinned/just-acted-on rows stay on top (see mergeQueue).
   */
  useEffect(() => {
    const items = listingsQuery.data?.items;
    if (!items) return;
    setQueue((current) => mergeQueue(toQueueEntries(items), current));
  }, [listingsQuery.data]);

  useEffect(() => {
    if (
      listingsQuery.error instanceof ApiError &&
      (listingsQuery.error.status === 401 || listingsQuery.error.status === 403)
    ) {
      setAccessDenied(true);
    }
  }, [listingsQuery.error]);

  /**
   * Apply an optimistic queue update after a successful action, then re-read the
   * feed so the server's view wins. The optimistic step keeps the row's badge
   * honest in the gap between the click and the refetch.
   */
  const persist = (next: QueueEntry[]) => {
    setQueue(next);
    void queryClient.invalidateQueries({ queryKey: ['admin-marketplace-listings'] });
  };

  /** A 401/403 from any action means the session lost admin rights. */
  const handleError = (error: unknown) => {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      setAccessDenied(true);
      return;
    }
    toast.error(
      t('marketplace.admin.queue.actionError', {
        error: error instanceof Error ? error.message : 'unknown',
      }),
    );
  };

  /** Refresh the cached public detail for the listings just acted on. */
  const invalidate = (listingIds: string[]) => {
    for (const listingId of listingIds) {
      void queryClient.invalidateQueries({ queryKey: ['marketplace-listing', listingId] });
    }
  };

  /**
   * Run one action per id sequentially. `allSettled` keeps a single failure from
   * cancelling the rest of a bulk run — a partial bulk still reports what worked.
   */
  const runBulk = async (
    listingIds: string[],
    action: (listingId: string) => Promise<unknown>,
  ): Promise<{ ok: string[]; failed: unknown[] }> => {
    const ok: string[] = [];
    const failed: unknown[] = [];
    for (const listingId of listingIds) {
      try {
        await action(listingId);
        ok.push(listingId);
      } catch (error) {
        failed.push(error);
      }
    }
    return { ok, failed };
  };

  const approveMutation = useMutation({
    mutationFn: (listingIds: string[]) => runBulk(listingIds, approveListing),
    onSuccess: ({ ok, failed }) => {
      if (ok.length > 0) {
        persist(ok.reduce((acc, id) => markQueueStatus(acc, id, 'active'), queue));
        toast.success(t('marketplace.admin.queue.approveSuccess'));
        invalidate(ok);
      }
      failed.forEach(handleError);
      setSelected([]);
    },
    onError: handleError,
  });

  const rejectMutation = useMutation({
    mutationFn: ({ listingIds, reason }: { listingIds: string[]; reason: string }) =>
      runBulk(listingIds, (listingId) => rejectListing(listingId, reason)),
    onSuccess: ({ ok, failed }) => {
      if (ok.length > 0) {
        persist(ok.reduce((acc, id) => markQueueStatus(acc, id, 'rejected'), queue));
        toast.success(t('marketplace.admin.queue.rejectSuccess'));
        invalidate(ok);
      }
      failed.forEach(handleError);
      setSelected([]);
    },
    onError: handleError,
  });

  const transitionMutation = useMutation({
    mutationFn: ({
      listingId,
      to,
      options,
    }: {
      listingId: string;
      to: ListingStatus;
      options: { adminOverride?: boolean; reason?: string };
    }) => transitionListing(listingId, to, options),
    onSuccess: (result, variables) => {
      persist(markQueueStatus(queue, variables.listingId, result.status ?? variables.to));
      toast.success(
        t('marketplace.admin.queue.transitionSuccess', {
          status: t(`marketplace.admin.queue.status.${result.status ?? variables.to}`),
        }),
      );
      invalidate([variables.listingId]);
    },
    onError: handleError,
  });

  if (accessDenied) return <AccessDenied />;

  const isBusy =
    approveMutation.isPending || rejectMutation.isPending || transitionMutation.isPending;

  /**
   * The tab count. Only the queue has one: own-stock and the admin's own
   * listings are not part of this feed, so those tabs are drawn without a
   * number rather than with a guessed one.
   */
  const tabCount = (value: Tab): string | null =>
    value === 'queue' ? String(listingsQuery.data?.total ?? queue.length) : null;

  return (
    <>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-[26px] leading-[1.1] font-semibold tracking-[-0.02em] lg:text-[30px]">
          {t('marketplace.admin.title')}
        </h1>
        <p className="mt-1.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:mt-2 lg:text-[11px] lg:tracking-[0.14em]">
          <span className="lg:hidden">{t('marketplace.admin.subtitleShort')}</span>
          <span className="hidden lg:inline">{t('marketplace.admin.subtitle')}</span>
        </p>
      </div>

      {/* ── Tabs ────────────────────────────────────────────────────────── */}
      <div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 lg:hidden">
        {TABS.map((value) => {
          const count = tabCount(value);
          return (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              data-testid={`marketplace-tab-${value}`}
              className={`h-9 shrink-0 rounded-full border px-3 text-[13px] font-semibold whitespace-nowrap ${
                tab === value
                  ? 'border-cf-brand bg-cf-brand text-white'
                  : 'border-cf-line bg-cf-surface text-cf-ink'
              }`}
            >
              {t(`marketplace.admin.tabs.${value}`)}
              {count !== null ? ` · ${count}` : ''}
            </button>
          );
        })}
      </div>
      <div role="tablist" className="hidden w-fit gap-0.5 rounded-cf-lg bg-cf-fill p-[3px] lg:flex">
        {TABS.map((value) => {
          const count = tabCount(value);
          return (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={`h-9 rounded-cf-md px-3.5 text-[13px] font-semibold whitespace-nowrap ${
                tab === value ? 'bg-cf-brand text-white' : 'bg-transparent text-cf-ink'
              }`}
            >
              {t(`marketplace.admin.tabs.${value}`)}{' '}
              {count !== null && (
                <span className="font-mono text-[11px] font-medium opacity-70">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'queue' ? (
        <ModerationQueueTab
          queue={queue}
          selected={selected}
          statusFilter={statusFilter}
          statuses={LISTING_STATUSES}
          onStatusFilterChange={setStatusFilter}
          isLoading={listingsQuery.isPending}
          isError={listingsQuery.isError}
          onRefresh={() => void listingsQuery.refetch()}
          onSelectedChange={setSelected}
          // Pinning is purely local: the fetched page cannot contain an id that
          // does not match the current status filter, and mergeQueue keeps it.
          onAdd={(listingId, note) => setQueue(addToQueue(queue, listingId, note))}
          onRemove={(listingId) => {
            // Unpin only — a listing in the backlog cannot be dismissed from the
            // client, so a server-fed row returns on the next fetch by design.
            setQueue(removeFromQueue(queue, listingId));
            setSelected((current) => current.filter((id) => id !== listingId));
          }}
          onApprove={(listingIds) => approveMutation.mutate(bulkTargets(queue, listingIds, 'active'))}
          onReject={(listingIds, reason) =>
            rejectMutation.mutate({ listingIds: bulkTargets(queue, listingIds, 'rejected'), reason })
          }
          onTransition={(listingId, to, options) =>
            transitionMutation.mutate({ listingId, to, options })
          }
          isBusy={isBusy}
        />
      ) : tab === 'seller' ? (
        <SellerListingsTab />
      ) : (
        <OwnStockManager
          onListingCreated={(listingId) =>
            // A fresh own-stock listing starts in `draft`, so the default
            // `moderation` feed will not carry it — pin it so the moderator can
            // drive it to active without hunting for the id.
            setQueue(addToQueue(queue, listingId, t('marketplace.admin.ownStock.queueNote')))
          }
        />
      )}
    </>
  );
}
